import { useEffect, useRef } from 'react';

const FRAMES = 60;

function buildFrameURLs() {
  const urls = [];
  for (let i = 0; i < FRAMES; i++) {
    urls.push(`/assets/seq_run/f${String(i).padStart(2, '0')}.webp`);
  }
  return urls;
}

/**
 * [모듈 4] 맨 위 스크롤 영상 히어로(.reveal-scroll).
 *
 * ▷ 무엇: 긴 스크롤 구간(.reveal-scroll, 기본 520vh) 안에서 .reveal-sticky가 화면에 핀
 *         고정된 채, 스크롤 진행률 p(0~1)에 맞춰 아래가 진행된다:
 *           p 0~0.10  : 흰 화면(빈 패널)
 *           p 0.10~0.85: 캐릭터 프레임(f00→f59)이 넘어가며 "달려와 카메라를 지나 빠져나감"
 *           p 0.84~   : 프레임 페이드아웃 → 완전 흰 패널
 *           p >0.90   : 로고가 "두둥 팍!"(pop 애니메이션, CSS @keyframes logoPop)
 *         패널은 시작에 알약(clip-path inset 42% + round 1000)에서 p 0~0.30 동안
 *         직사각형(inset 0 + round 24)으로 펼쳐지고 살짝 확대(scale 0.82→1)된다.
 *
 * ▷ 왜 <video>가 아니라 이미지 프레임 시퀀스인가: 스크롤로 영상을 긁으려면
 *   video.currentTime을 조작해야 하는데 (1) 정적 서버가 HTTP Range를 지원 안 하면 seek
 *   불가, (2) 모바일 사파리는 currentTime 스크럽이 불안정하다. 그래서 영상을 프레임
 *   이미지(/assets/seq_run/f00~f59.webp)로 뽑아 프리로드 후 스크롤에 맞춰 <img>.src만
 *   갈아끼운다.
 *
 * ▷ 성능: scroll/resize 때만 rAF 루프를 돌리고(kick), lerp 스무딩으로 부드럽게 따라온다.
 * ▷ prefers-reduced-motion: 애니메이션 없이 패널 펼친 채 캐릭터 한 프레임만 표시.
 */
export default function useRevealShowcase(scrollRef, stageRef, panelRef, logoRef, frameRef) {
  const frameURLsRef = useRef(null);
  if (!frameURLsRef.current) frameURLsRef.current = buildFrameURLs();

  useEffect(() => {
    const scroll = scrollRef.current;
    const stage = stageRef.current;
    const panel = panelRef.current;
    const logo = logoRef.current;
    const frameEl = frameRef.current;
    if (!scroll || !stage || !panel || !logo) return undefined;

    const frameURLs = frameURLsRef.current;
    frameURLs.forEach((u) => {
      const pre = new Image();
      pre.src = u;
    });

    const clamp = (v) => Math.min(1, Math.max(0, v));
    let lastFrame = -1;
    let logoShown = false;

    function apply(p) {
      const e = clamp(p / 0.3); // 패널 펼침·확대는 앞 30%에서 완료
      const inset = 42 * (1 - e); // 42%(알약) → 0%(꽉 참)
      const round = 1000 - (1000 - 24) * e; // 1000px → 24px
      panel.style.clipPath = `inset(${inset}% ${inset}% round ${round}px)`;
      stage.style.transform = `scale(${0.82 + 0.18 * e})`;
      if (frameEl) {
        const idx = p < 0.1 ? FRAMES - 1 : Math.round(clamp((p - 0.1) / 0.75) * (FRAMES - 1));
        if (idx !== lastFrame) {
          frameEl.src = frameURLs[idx];
          lastFrame = idx;
        }
        frameEl.style.opacity = String(1 - clamp((p - 0.84) / 0.06));
      }
      if (p > 0.9 && !logoShown) {
        logo.classList.add('pop');
        logoShown = true;
      } else if (p < 0.82 && logoShown) {
        logo.classList.remove('pop');
        logoShown = false;
      }
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      panel.style.clipPath = 'inset(0% round 24px)';
      stage.style.transform = 'none';
      if (frameEl) frameEl.src = frameURLs[4]; // 캐릭터 한 프레임 표시
      return undefined;
    }

    function progress() {
      const total = scroll.offsetHeight - window.innerHeight;
      if (total <= 0) return 0;
      return clamp(-scroll.getBoundingClientRect().top / total);
    }

    let current = 0;
    let ticking = false;
    let rafId = null;

    function frameLoop() {
      const target = progress();
      current += (target - current) * 0.085; // lerp 스무딩
      const done = Math.abs(target - current) < 0.0005;
      if (done) current = target;
      apply(current);
      if (!done) {
        rafId = requestAnimationFrame(frameLoop);
      } else {
        ticking = false;
      }
    }

    function kick() {
      if (!ticking) {
        ticking = true;
        rafId = requestAnimationFrame(frameLoop);
      }
    }

    apply(0);
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);
    kick();

    return () => {
      window.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollRef, stageRef, panelRef, logoRef, frameRef]);
}
